import { useEffect, useRef } from 'react';

import { feedAmountForHold, nursingAmountForHold, resolveGestureSelection } from '../lib/gesture.js';

const LONG_PRESS_MS = 450;
const MOVE_TOLERANCE = 12;

export default function GestureAction({
  kind,
  label,
  hint,
  icon: Icon,
  className = '',
  disabled = false,
  onTap,
  onGestureChange,
  onComplete,
  quickSettings,
}) {
  const interaction = useRef(null);

  function amountFor(feedType, elapsed) {
    const settings = quickSettings || {};
    if (feedType === 'nursing') return nursingAmountForHold(elapsed, { ...settings, initial: settings.nursingInitial, max: settings.nursingMax ?? 180 });
    return feedAmountForHold(elapsed, {
      ...settings,
      initial: feedType === 'breastmilk' ? settings.breastmilkInitial : settings.formulaInitial,
    });
  }

  function cleanup({ notify = true } = {}) {
    const current = interaction.current;
    if (!current) return;
    clearTimeout(current.armTimer);
    clearInterval(current.amountTimer);
    interaction.current = null;
    if (notify) onGestureChange(null);
  }

  useEffect(() => () => cleanup({ notify: false }), []);

  useEffect(() => {
    function cancelOnHidden() {
      if (document.hidden) cleanup();
    }
    document.addEventListener('visibilitychange', cancelOnHidden);
    return () => document.removeEventListener('visibilitychange', cancelOnHidden);
  }, []);

  function publish(current) {
    const nursing = kind === 'feed' && current.feedType === 'nursing';
    const amount = kind === 'feed' && current.selected && current.selected !== 'cancel' && (!nursing || current.side)
      ? (nursing
        ? amountFor('nursing', performance.now() - current.selectedAt)
        : amountFor(current.feedType, performance.now() - current.selectedAt))
      : null;
    onGestureChange({ kind, selected: current.selected, feedType: current.feedType, side: current.side, amount });
  }

  function handlePointerDown(event) {
    if (disabled || event.button !== 0 || interaction.current) return;
    event.currentTarget.setPointerCapture(event.pointerId);
    const current = {
      pointerId: event.pointerId,
      startX: event.clientX,
      startY: event.clientY,
      armed: false,
      selected: null,
      feedType: null,
      side: null,
      selectedAt: 0,
      amountTimer: null,
      armTimer: null,
    };
    current.armTimer = setTimeout(() => {
      if (interaction.current !== current) return;
      current.armed = true;
      if (navigator.vibrate) navigator.vibrate(18);
      publish(current);
      current.amountTimer = setInterval(() => publish(current), 100);
    }, LONG_PRESS_MS);
    interaction.current = current;
  }

  function handlePointerMove(event) {
    const current = interaction.current;
    if (!current || current.pointerId !== event.pointerId) return;
    const distance = Math.hypot(event.clientX - current.startX, event.clientY - current.startY);
    if (!current.armed) {
      if (distance > MOVE_TOLERANCE) cleanup();
      return;
    }
    const phase = kind === 'feed' && current.feedType === 'nursing' ? 'side' : 'primary';
    const selected = resolveGestureSelection(kind, event.clientX, event.clientY, window.innerWidth, window.innerHeight, phase);
    const feedType = kind === 'feed' && phase === 'primary' && ['breastmilk', 'formula', 'nursing'].includes(selected)
      ? selected
      : current.feedType;
    const side = kind === 'feed' && phase === 'side' && ['left', 'right'].includes(selected)
      ? selected
      : phase === 'primary' && feedType !== 'nursing' ? null : current.side;
    if (selected !== current.selected || feedType !== current.feedType || side !== current.side) {
      current.selected = selected;
      current.feedType = feedType;
      current.side = side;
      current.selectedAt = performance.now();
      if (navigator.vibrate && selected && selected !== 'cancel') navigator.vibrate(10);
      publish(current);
    }
  }

  function handlePointerUp(event) {
    const current = interaction.current;
    if (!current || current.pointerId !== event.pointerId) return;
    if (!current.armed) {
      cleanup();
      onTap?.();
      return;
    }
    const selected = current.selected;
    const feedType = current.feedType || selected;
    const validFeed = kind === 'feed' && feedType === 'nursing'
      ? ['left', 'right'].includes(selected) && ['left', 'right'].includes(current.side)
      : kind === 'feed' && ['breastmilk', 'formula'].includes(selected) && ['breastmilk', 'formula'].includes(feedType);
    const amount = kind === 'feed' && validFeed
      ? (feedType === 'nursing'
        ? amountFor('nursing', performance.now() - current.selectedAt)
        : amountFor(feedType, performance.now() - current.selectedAt))
      : selected;
    cleanup();
    if (kind === 'feed') {
      if (validFeed) onComplete({ kind, selected: feedType, side: current.side, amount });
    } else if (selected && selected !== 'cancel') onComplete({ kind, selected, amount });
  }

  return (
    <button
      type="button"
      className={`quick-action quick-action--${kind} ${className}`}
      disabled={disabled}
      onPointerDown={handlePointerDown}
      onPointerMove={handlePointerMove}
      onPointerUp={handlePointerUp}
      onPointerCancel={() => cleanup()}
      onContextMenu={(event) => event.preventDefault()}
      aria-label={`${label}，点击快速记录，长按调整`}
    >
      <span className="quick-action__icon" aria-hidden="true"><Icon size={28} strokeWidth={2.2} /></span>
      <span className="quick-action__copy">
        <strong>{label}</strong>
        <small>{hint}</small>
      </span>
    </button>
  );
}
